import React, { forwardRef, useCallback, useMemo } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db from '../../lib/db';
import { searchEmojis } from '../../lib/emoji-search/emojiSearch';
import { currentWorkspaceId } from '../../lib/meta';

import { EmojiEntity } from '../EmojiEntity';
import { SuggestionList } from './SuggestionList';

export const EmojiList = forwardRef(({ command, query }, ref) => {
  const items = useMemo(() => {
    return searchEmojis(query, {
      limit: 5,
      sortByRelevance: true,
    });
  }, [query]);

  // Chat titles so custom emojis from the user's other chats can say where
  // they're from (same shortcode can exist in several chats).
  const workspaceTitles = useLiveQuery(async () => new Map((await db.workspaces.toArray()).map((w) => [w.id, w.title])), [], null);

  const handleSelect = useCallback(
    (emoji) => {
      command({
        id: emoji.id,
        custom: emoji.kind === 'custom',
        name: emoji.shortcode,
        image: emoji.kind === 'custom' ? emoji.public_url : null,
      });
    },
    [command],
  );

  const renderItem = useCallback(
    (emoji) => {
      const sourceLabel =
        emoji.kind === 'custom' && emoji.workspace_id !== currentWorkspaceId() ? workspaceTitles?.get(emoji.workspace_id) : null;

      return (
        <>
          <div className="flex-shrink-0">
            <EmojiEntity emoji={emoji} suggestion={true} />
          </div>
          <span className="truncate font-medium text-neutral-800 dark:text-neutral-200">:{emoji.shortcode}:</span>
          {sourceLabel && (
            <span className="ml-auto max-w-40 shrink-0 truncate pl-3 text-xs font-medium text-gray-600 dark:text-neutral-300">
              {sourceLabel}
            </span>
          )}
        </>
      );
    },
    [workspaceTitles],
  );

  return (
    <SuggestionList
      ref={ref}
      items={items}
      onSelect={handleSelect}
      renderItem={renderItem}
      itemClassName="min-w-0 flex-nowrap gap-1.5"
      query={query}
    />
  );
});

EmojiList.displayName = 'EmojiList';
